import React, { useState, useEffect, useCallback, useRef } from "react";

import {
  listHistorianProviders,
  createHistorianProvider,
  updateHistorianProvider,
  deleteHistorianProvider,
  testHistorianProvider,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import NumberInput from "../../../../components/NumberInput";
import PasswordInput from "../../../../components/PasswordInput";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";

// Provider kinds the form can create. default_local is the seeded built-in and
// is not creatable from the UI; its only editable setting is retention.
const KIND_OPTIONS = [{ value: "timescale", label: "TimescaleDB or Tiger Cloud" }];
const DEFAULT_LOCAL_PROVIDER_LABEL = "Control Seat Local Historian";

const KIND_LABELS = {
  default_local: DEFAULT_LOCAL_PROVIDER_LABEL,
  timescale: "TimescaleDB or Tiger Cloud",
};

const KIND_ICONS = {
  default_local: "CS",
  timescale: "TS",
};

const SSL_MODES = [
  { value: "disable", label: "Disable" },
  { value: "allow", label: "Allow" },
  { value: "prefer", label: "Prefer" },
  { value: "require", label: "Require" },
  { value: "verify-ca", label: "Verify CA" },
  { value: "verify-full", label: "Verify Full" },
];

const RETENTION_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "0", label: "Forever" },
];

const STATUS_COLORS = {
  ok: { bg: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)", border: "rgba(76,175,80,0.25)", dot: "#4caf50" },
  testing: { bg: "rgba(255,193,7,0.12)", color: "rgba(255,193,7,0.95)", border: "rgba(255,193,7,0.25)", dot: "#ffc107" },
  failed: { bg: "rgba(244,67,54,0.12)", color: "rgba(244,67,54,0.95)", border: "rgba(244,67,54,0.25)", dot: "#f44336" },
  unknown: { bg: "rgba(158,158,158,0.12)", color: "rgba(200,200,200,0.95)", border: "rgba(158,158,158,0.25)", dot: "#9e9e9e" },
};

const StatusDot = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span title={status} style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
  );
};

const errorMessage = (err, fb) => {
  const m = String(err?.message || err).trim();
  return m || fb;
};

// shortError collapses a raw driver error (often a multi-line pgx dial dump
// with IPv6/IPv4 addresses) to a short inline label; the full text stays in
// the row tooltip. Prefers the innermost ": "-delimited cause.
const shortError = (msg) => {
  if (!msg) return "Connection failed";
  const raw = String(msg);
  if (/connection refused/i.test(raw)) return "Connection refused";
  if (/timeout|deadline exceeded/i.test(raw)) return "Connection timed out";
  if (/no such host|could not translate host name/i.test(raw)) return "Host not found";
  if (/authentication failed|password authentication failed|28P01/i.test(raw)) return "Authentication failed";
  if (/database .* does not exist|3D000/i.test(raw)) return "Database not found";
  if (/certificate|ssl|tls/i.test(raw)) return "TLS/SSL error";

  const first = raw.split("\n")[0].trim();
  const parts = first.split(": ");
  const tail = (parts[parts.length - 1] || first).trim();
  return tail.length > 80 ? `${tail.slice(0, 79)}…` : tail;
};

const providerStatusSignature = (provider) => JSON.stringify({
  kind: provider?.kind || "",
  live: provider?.live === true,
  hasPassword: !!provider?.has_password,
  updatedAt: provider?.updated_at || "",
  config: provider?.config || {},
});

const isBuiltInProvider = (provider) => provider?.id === "default_local" || provider?.kind === "default_local";

const retentionDaysFor = (provider) => {
  const raw = provider?.config?.retention_days;
  if (raw === undefined || raw === null || raw === "") return 30;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 30;
};

const retentionLabelFor = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return "30 days";
  if (n === 0) return "Forever";
  if (n === 1) return "1 day";
  return RETENTION_OPTIONS.find((o) => o.value === String(n))?.label || `${Math.trunc(n)} days`;
};

const liveStatusFor = (provider) => {
  if (provider?.live === true) return "ok";
  if (provider?.live === false) return "failed";
  return "unknown";
};

const omitProviderIds = (obj, ids) => {
  let changed = false;
  const next = { ...obj };
  ids.forEach((id) => {
    if (Object.prototype.hasOwnProperty.call(next, id)) {
      delete next[id];
      changed = true;
    }
  });
  return changed ? next : obj;
};

// ── Create/edit modal ───────────────────────────────────────────────

const INITIAL_FORM = {
  name: "",
  kind: "timescale",
  // timescale config fields
  host: "",
  port: "5432",
  database: "tsdb",
  user: "tsdbadmin",
  password: "",
  sslmode: "require",
  retentionDays: "0",
};

const ProviderModal = ({ editing, prefilled, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (editing) {
      const cfg = editing.config || {};
      return {
        name: editing.name || "",
        kind: editing.kind || "timescale",
        host: cfg.host || "",
        port: String(cfg.port || 5432),
        database: cfg.database || "tsdb",
        user: cfg.user || "tsdbadmin",
        password: "", // never returned by API
        sslmode: cfg.sslmode || "require",
        retentionDays: String(cfg.retention_days || 0),
      };
    }
    // AI-proposed create: prefill the connection fields (never the password).
    if (prefilled) {
      return {
        ...INITIAL_FORM,
        name: prefilled.name || "",
        host: prefilled.host || "",
        port: prefilled.port != null ? String(prefilled.port) : INITIAL_FORM.port,
        database: prefilled.database || INITIAL_FORM.database,
        user: prefilled.user || INITIAL_FORM.user,
        sslmode: prefilled.sslmode || INITIAL_FORM.sslmode,
        retentionDays: prefilled.retention_days != null ? String(prefilled.retention_days) : INITIAL_FORM.retentionDays,
      };
    }
    return INITIAL_FORM;
  });
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const isEdit = !!editing;

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : (e?.target?.type === "checkbox" ? e.target.checked : e.target.value);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // buildConfig assembles the kind-specific JSONB config the backend
  // validates. Only timescale carries config today; default_local is not
  // creatable from this form.
  const buildConfig = () => ({
    host: form.host,
    port: parseInt(form.port, 10) || 5432,
    database: form.database,
    user: form.user,
    sslmode: form.sslmode,
    retention_days: parseInt(form.retentionDays, 10) || 0,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      name: form.name,
      config: buildConfig(),
    };

    if (isEdit) {
      // PATCH semantics: only send the password when the user typed one.
      // An empty string would clear the existing secret.
      if (form.password) payload.password = form.password;
    } else {
      payload.kind = form.kind;
      payload.password = form.password;
    }

    try {
      if (isEdit) {
        await updateHistorianProvider(editing.id, payload);
      } else {
        await createHistorianProvider(payload);
      }
      notify({ type: "success", title: isEdit ? "Updated" : "Created", message: `Historian "${form.name}" ${isEdit ? "updated" : "created"}.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: isEdit ? "Update Failed" : "Create Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 560 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name || editing.id}` : "New Historian Provider"}</div>
            <div className="gwModalSubtitle">{isEdit ? "Update the historian connection." : "Connect an external historian for per-tag history storage."}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input className="gwInput" placeholder="e.g. tiger-cloud-prod" value={form.name} onChange={set("name")} autoFocus={!isEdit} required />
            <div className="gwHint">Display name. Must be unique (case-insensitive).</div>
          </div>
          {!isEdit && (
            <div className="gwField">
              <label className="gwLabel">Type</label>
              <Select value={form.kind} onChange={set("kind")} options={KIND_OPTIONS} />
              <div className="gwHint">The Control Seat local historian is built in and always available; add an external backend here.</div>
            </div>
          )}

          <div className="gwFieldRow">
            <div className="gwField" style={{ flex: 3 }}>
              <label className="gwLabel">Host</label>
              <input className="gwInput" placeholder="e.g. abc.tsdb.cloud.timescale.com" value={form.host} onChange={set("host")} required />
            </div>
            <div className="gwField" style={{ flex: 1 }}>
              <label className="gwLabel">Port</label>
              <NumberInput value={form.port} onChange={set("port")} min={1} max={65535} />
            </div>
          </div>
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">Database</label>
              <input className="gwInput" placeholder="tsdb" value={form.database} onChange={set("database")} required />
            </div>
            <div className="gwField">
              <label className="gwLabel">SSL Mode</label>
              <Select value={form.sslmode} onChange={set("sslmode")} options={SSL_MODES} />
            </div>
          </div>
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">User</label>
              <input className="gwInput" placeholder="tsdbadmin" value={form.user} onChange={set("user")} required />
            </div>
            <div className="gwField">
              <label className="gwLabel">Password</label>
              <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : ""} value={form.password} onChange={set("password")} />
            </div>
          </div>
          <div className="gwField">
            <label className="gwLabel">Retention (days)</label>
            <NumberInput value={form.retentionDays} onChange={set("retentionDays")} min={0} step={1} placeholder="0" />
            <div className="gwHint">Provider-wide chunk pruning. 0 keeps data forever. Per-tag retention is not available on a shared hypertable.</div>
          </div>
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create Provider"}
          </button>
        </div>
      </form>
    </div>
  );
};

const BuiltInRetentionModal = ({ provider, canConfigure, onClose, onSaved }) => {
  const [retentionDays, setRetentionDays] = useState(() => String(retentionDaysFor(provider)));
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const displayName = provider?.name || provider?.id || DEFAULT_LOCAL_PROVIDER_LABEL;

  // Shrinking the window (or moving off Forever) permanently deletes history
  // older than the new value on the next restart. Treat 0/Forever as infinite.
  const oldDays = retentionDaysFor(provider);
  const parsedDays = Number(retentionDays);
  const newDays = Number.isFinite(parsedDays) && parsedDays >= 0 ? Math.trunc(parsedDays) : 0;
  const willDeleteHistory = newDays > 0 && (oldDays === 0 || newDays < oldDays);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canConfigure) return;

    const parsed = Number(retentionDays);
    const normalized = Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : 0;

    setSubmitting(true);
    try {
      await updateHistorianProvider(provider.id, { config: { retention_days: normalized } });
      notify({ type: "success", title: "Retention updated", message: "Applies after the next gateway restart." });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Update Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 460 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Edit: {displayName}</div>
            <div className="gwModalSubtitle">Configure the built-in historian.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Retention (days)</label>
            <NumberInput
              value={retentionDays}
              onChange={(e) => setRetentionDays(e.target.value)}
              min={0}
              step={1}
              placeholder="30"
              required
              disabled={!canConfigure}
            />
            <div className="gwHint">0 keeps data forever. Changes apply after the next gateway restart.</div>
            {willDeleteHistory && (
              <div className="gwHint" style={{ color: "var(--gw-danger)", marginTop: 8 }}>
                Reducing retention to {retentionLabelFor(newDays)} permanently deletes history older than that on the next gateway restart. This can't be undone.
              </div>
            )}
          </div>
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button
            className={willDeleteHistory ? "gwDangerBtn" : "gwPrimaryBtn"}
            type="submit"
            disabled={!canConfigure || submitting}
          >
            {submitting ? "Saving..." : willDeleteHistory ? "Reduce & delete older history" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────

export const HistorianProvidersPage = () => {
  const canConfigure = usePermission("configure");
  const [providers, setProviders] = useState([]);
  const [statuses, setStatuses] = useState({}); // id → "ok" | "testing" | "failed"
  const [errorsById, setErrorsById] = useState({}); // id → last test error
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [proposed, setProposed] = useState(null); // AI-proposed create prefill
  const [editing, setEditing] = useState(null);
  const [editingBuiltIn, setEditingBuiltIn] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const providerStatusSignaturesRef = useRef(new Map());
  const notify = useNotify();

  const reconcileStatusCache = useCallback((list) => {
    const prev = providerStatusSignaturesRef.current;
    const next = new Map(list.map((p) => [p.id, providerStatusSignature(p)]));
    const stale = new Set();

    prev.forEach((sig, id) => {
      if (!next.has(id) || next.get(id) !== sig) stale.add(id);
    });

    providerStatusSignaturesRef.current = next;
    if (stale.size === 0) return;

    setStatuses((p) => omitProviderIds(p, stale));
    setErrorsById((p) => omitProviderIds(p, stale));
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const list = (await listHistorianProviders()) || [];
      reconcileStatusCache(list);
      setProviders(list);
    } catch (err) {
      notify({ type: "error", title: "Error", message: errorMessage(err, "Failed to load historian providers") });
    } finally {
      setLoading(false);
    }
  }, [notify, reconcileStatusCache]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Refresh when the AI mutates a historian provider.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "historian_providers") fetchAll();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [fetchAll]);

  // Open the create modal pre-filled when the AI proposes a historian provider.
  useEffect(() => {
    const handler = (e) => {
      setProposed(e.detail?.payload || {});
      setShowCreate(true);
    };
    window.addEventListener("gw-ai-propose-historian-provider-page", handler);
    return () => window.removeEventListener("gw-ai-propose-historian-provider-page", handler);
  }, []);

  // runTest is intentionally operator-triggered. The page uses the server's
  // live-store flag for passive status so it does not continuously ping external
  // historians while someone leaves this page open.
  const runTest = useCallback(async (id, { name } = {}) => {
    setStatuses((p) => ({ ...p, [id]: "testing" }));
    setErrorsById((p) => ({ ...p, [id]: undefined }));
    const label = name || id;
    try {
      const res = await testHistorianProvider(id);
      if (res?.ok) {
        setStatuses((p) => ({ ...p, [id]: "ok" }));
        setErrorsById((p) => ({ ...p, [id]: undefined }));
        notify({ type: "success", title: "Connection OK", message: `Historian "${label}" is reachable.` });
      } else {
        const msg = res?.error || "Connection failed";
        setStatuses((p) => ({ ...p, [id]: "failed" }));
        setErrorsById((p) => ({ ...p, [id]: msg }));
        notify({ type: "error", title: "Connection Failed", message: shortError(msg) });
      }
    } catch (err) {
      const msg = errorMessage(err, "Connection failed");
      setStatuses((p) => ({ ...p, [id]: "failed" }));
      setErrorsById((p) => ({ ...p, [id]: msg }));
      notify({ type: "error", title: "Connection Failed", message: shortError(msg) });
    }
  }, [notify]);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteHistorianProvider(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `Historian "${deleteTarget.name || deleteTarget.id}" deleted.` });
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      notify({ type: "error", title: "Delete Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setDeleting(false);
    }
  };

  const builtInProviders = providers.filter(isBuiltInProvider);
  const additionalProviders = providers.filter((p) => !isBuiltInProvider(p));
  const hasProviders = providers.length > 0;

  const renderProviderRow = (p) => {
    const status = statuses[p.id] || liveStatusFor(p);
    const colors = STATUS_COLORS[status] || STATUS_COLORS.unknown;
    const isBuiltIn = isBuiltInProvider(p);
    const cfg = p.config || {};
    const displayName = p.name || p.id;
    const retentionText = retentionLabelFor(retentionDaysFor(p));
    const meta = isBuiltIn
      ? `Built-in managed backend · retention: ${retentionText}`
      : `${cfg.host || "?"}${cfg.database ? ` / ${cfg.database}` : ""}${p.has_password ? " · auth" : ""}`;
    return (
      <div key={p.id} data-gw-resource="historian_providers" data-gw-id={p.id} data-gw-name={displayName}>
        <div className="umUserRow">
          <div
            style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700, color: colors.color,
            }}
          >
            {KIND_ICONS[p.kind] || "H"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="umUserName">{displayName}</span>
              {status !== "unknown" && <StatusDot status={status} />}
            </div>
            <div className="umUserMeta">
              {KIND_LABELS[p.kind] || p.kind} · {meta}
              {errorsById[p.id] && (
                <span style={{ color: "var(--gw-danger)" }} title={errorsById[p.id]}> &middot; {shortError(errorsById[p.id])}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="gwGhostBtn"
            style={{ flexShrink: 0, padding: "4px 10px", fontSize: 11 }}
            onClick={() => runTest(p.id, { name: displayName })}
            disabled={!canConfigure || status === "testing"}
            title={canConfigure ? undefined : "Requires configure permission"}
          >
            {status === "testing" ? "Testing..." : "Test"}
          </button>
          <button
            type="button"
            className="gwIconButton"
            style={{ flexShrink: 0 }}
            onClick={(e) => openMenu(e, p.id)}
          >
            <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
          </button>
          {menuOpen === p.id && (
            <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
              <button
                className="gwProjectMenuItem"
                type="button"
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                onClick={() => {
                  setMenuOpen(null);
                  if (isBuiltIn) setEditingBuiltIn(p);
                  else setEditing(p);
                }}
              >
                Edit
              </button>
              {!isBuiltIn && (
                <>
                  <div className="gwProfileDivider" />
                  <button
                    className="gwProjectMenuItem gwProjectMenuDanger"
                    type="button"
                    disabled={!canConfigure}
                    title={canConfigure ? undefined : "Requires configure permission"}
                    onClick={() => { setMenuOpen(null); setDeleteTarget(p); }}
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Historians</div>
            <div className="gwPageSubtitle">
              {hasProviders ? `${providers.length} historian${providers.length !== 1 ? "s" : ""} configured` : "Connect external historians for per-tag history storage."}
            </div>
          </div>
          <button
            className="gwPrimaryBtn"
            type="button"
            onClick={() => setShowCreate(true)}
            disabled={!canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
            <span>Add Historian</span>
          </button>
        </div>

        {loading && !hasProviders && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading historians...</div>
        )}

        {!loading && !hasProviders && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No historians yet</div>
            <div className="gwEmptyBody">Add a TimescaleDB or Tiger Cloud historian to store selected tags' history outside the Control Seat local historian.</div>
          </div>
        )}

        {hasProviders && builtInProviders.length > 0 && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Built-in</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {builtInProviders.map(renderProviderRow)}
            </div>
          </div>
        )}

        {hasProviders && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Additional Historians</div>
            {additionalProviders.length > 0 ? (
              <div className="gwCardSectionBody" style={{ padding: 0 }}>
                {additionalProviders.map(renderProviderRow)}
              </div>
            ) : (
              <div className="gwCardSectionBody">
                <div className="gwEmptyState gwEmptyCompact">
                  <div className="gwEmptyTitle">No additional historians</div>
                  <div className="gwEmptyBody">Add an external historian when a tag needs history outside the built-in historian.</div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {showCreate && (
        <ProviderModal
          prefilled={proposed}
          onClose={() => { setShowCreate(false); setProposed(null); }}
          onSaved={fetchAll}
        />
      )}
      {editing && (
        <ProviderModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            // Config changed — any cached test result is stale. Clear it so the
            // row drops to "untested" until the operator re-tests.
            const id = editing.id;
            setStatuses((p) => { const n = { ...p }; delete n[id]; return n; });
            setErrorsById((p) => { const n = { ...p }; delete n[id]; return n; });
            fetchAll();
          }}
        />
      )}
      {editingBuiltIn && (
        <BuiltInRetentionModal
          provider={editingBuiltIn}
          canConfigure={canConfigure}
          onClose={() => setEditingBuiltIn(null)}
          onSaved={fetchAll}
        />
      )}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Historian</div>
                <div className="gwModalSubtitle">Tags must be moved off this historian before it can be removed.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove historian <strong>{deleteTarget.name || deleteTarget.id}</strong>? History already written there is not deleted, but becomes inaccessible from tag queries.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Historian"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
